// Login step components — the EXACT same layout the console (pages/Login.js) uses, extracted
// so the public portal (/pub) renders an identical login instead of a look-alike. Presentational
// only: each step calls the handlers its parent passes, so the console (session cookie) and the
// portal (signed step tokens) can drive the same UI with their own flows.
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useI18n } from "i18n";

export function EmailStep({ email, setEmail, onSubmit, loading, error, oidcEnabled, onFaceLogin, faceLoading }) {
  const { t } = useI18n();
  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.system.title")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.system.emailSubtitle")}
        </p>
      </CardHeader>
      <CardContent>
        {onFaceLogin && (
          <>
            <button
              type="button"
              onClick={onFaceLogin}
              disabled={faceLoading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" />
                <path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" /><path d="M9 9h.01" /><path d="M15 9h.01" />
              </svg>
              {faceLoading ? t("login.face.starting", "Iniciando…") : t("login.face.button", "Entrar com o rosto")}
            </button>
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase">{t("login.or", "ou")}</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
          </>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("login.email.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error}
              required
              autoFocus
              autoComplete="email"
              className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
            />
            {error && (
              <p className="text-xs text-red-500 mt-1">{error}</p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.continue")}
          </Button>
        </form>
        {oidcEnabled && (
          <div className="mt-4">
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase">{t("login.or")}</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            <a
              href="/oidc/login"
              className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-white border border-slate-300 px-3.5 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors duration-150"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              {t("login.oidc")}
            </a>
          </div>
        )}
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-xs text-center text-gray-500 leading-relaxed max-w-[280px]">
          {t("login.system.emailHelp")}{" "}
          <a
            href="mailto:contato@sec4us.com.br"
            className="text-blue-600 hover:underline font-medium"
          >
            contato@sec4us.com.br
          </a>
        </p>
      </CardFooter>
    </div>
  );
}

export function MfaStep({ code, setCode, onSubmit, onBack, loading, error }) {
  const { t } = useI18n();
  const inputRefs = useRef([]);
  const autoSubmitRef = useRef(false);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (code.every((d) => d !== "") && !autoSubmitRef.current && !loading) {
      autoSubmitRef.current = true;
      onSubmit(code.join(""));
    }
    if (code.some((d) => d === "")) {
      autoSubmitRef.current = false;
    }
  }, [code, onSubmit, loading]);

  const handleChange = useCallback(
    (index, value) => {
      if (!/^\d*$/.test(value)) return;
      const newCode = [...code];
      newCode[index] = value.slice(-1);
      setCode(newCode);
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code, setCode]
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (pasted.length > 0) {
        const newCode = [...code];
        for (let i = 0; i < pasted.length && i < 6; i++) {
          newCode[i] = pasted[i];
        }
        setCode(newCode);
        const focusIndex = Math.min(pasted.length, 5);
        inputRefs.current[focusIndex]?.focus();
      }
    },
    [code, setCode]
  );

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.system.title")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.mfa.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-center gap-3" onPaste={handlePaste}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-12 h-14 text-center text-xl font-semibold text-gray-900 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    error
                      ? "border-red-400 bg-red-50"
                      : code[index]
                      ? "border-blue-400 bg-white"
                      : "border-gray-200 bg-gray-100"
                  }`}
                  autoComplete="one-time-code"
                />
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-500 text-center mt-2">{error}</p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.mfa.submit")}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex-col items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          {t("login.back")}
        </button>
        <p className="text-xs text-gray-500">
          {t("login.mfa.notReceived")}{" "}
          <button
            type="button"
            className="text-blue-600 hover:underline font-medium"
            onClick={onBack}
          >
            {t("login.mfa.resend")}
          </button>
        </p>
      </CardFooter>
    </div>
  );
}

export function PasswordStep({ password, setPassword, onSubmit, onBack, loading, error }) {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.system.title")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.system.passwordSubtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">{t("login.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("login.password.placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!error}
                required
                autoFocus
                autoComplete="current-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.submit")}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          {t("login.back")}
        </button>
      </CardFooter>
    </div>
  );
}
